// Réseau (arbre), Mon compte, Invitations en attente
import { useState, useRef } from "react";
import { MaCotisationBloc } from "./MaCotisationBloc";
import { QRCodeCanvas } from "qrcode.react";
import { auth, conseillersApi } from "./api";
import { C, Btn, Input, Modal, FormField, Alert, Section, Spinner, useAsync } from "./shared";
import TreeNode from "./TreeNode";

// ── Arbre du réseau ──────────────────────────────────────────────────────────
export const NetworkTree = () => {
  const { data, loading } = useAsync(conseillersApi.tree, []);
  if (loading) return <Spinner />;
  const roots = data?.tree || [];
  return (
    <>
      <Alert type="info">
        <span>🌐</span>
        <span>Arborescence de votre réseau sur <strong>3 niveaux</strong> · {data?.total || 0} consultant(s) au total</span>
      </Alert>
      {roots.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Aucun consultant enregistré</div>
      ) : (
        <TreeNode roots={roots} />
      )}
    </>
  );
};

// ── Lien personnel (Étape 16) ────────────────────────────────────────────────
const MonLienPerso = ({ code }) => {
  const [ok, setOk] = useState(false);
  const ref = useRef(null);
  const url = `${window.location.origin}/r/${code}`;
  const copy = async () => { try { await navigator.clipboard.writeText(url); setOk(true); setTimeout(() => setOk(false), 2000); } catch { /* ignore */ } };
  const dl = () => {
    const cv = ref.current?.querySelector("canvas");
    if (!cv) return;
    const a = document.createElement("a"); a.href = cv.toDataURL("image/png"); a.download = `qr-auprea-${code}.png`; a.click();
  };
  return (
    <Section title="Mon lien personnel" icon="🔗" defaultOpen>
      <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
        <div ref={ref} style={{ padding: 10, border: `2px solid ${C.gold}`, borderRadius: 10, background: "#fff" }} data-testid="lien-qr">
          <QRCodeCanvas value={url} size={150} fgColor={C.navy} includeMargin />
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 12, color: C.mid, marginBottom: 8 }}>Partagez ce lien ou ce QR code (cartes de visite, flyers) : chaque demande crée un lead « Nouveau » chez vous.</div>
          <Input value={url} readOnly data-testid="lien-url" style={{ background: C.gray2 }} />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Btn variant="gold" onClick={copy} data-testid="lien-copier">{ok ? "✓ Copié" : "Copier"}</Btn>
            <Btn onClick={dl} data-testid="lien-qr-download">⬇ Télécharger le QR code</Btn>
          </div>
        </div>
      </div>
    </Section>
  );
};

// ── Mon compte ───────────────────────────────────────────────────────────────
export const MonCompte = ({ user, myConseiller, onRefresh }) => {
  const [tel, setTel] = useState(myConseiller?.tel || "");
  const [photo, setPhoto] = useState(myConseiller?.photo || "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [pwd, setPwd] = useState({ current: "", new: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);

  const saveProfile = async () => {
    setSaving(true); setMsg(null);
    try {
      await auth.updateProfile({ tel, photo });
      setMsg({ type: "success", text: "Profil mis à jour" });
      onRefresh && onRefresh();
    } catch (e) { setMsg({ type: "error", text: e?.response?.data?.detail || "Erreur" }); }
    finally { setSaving(false); }
  };

  const onPhotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) return setMsg({ type: "error", text: "Photo trop lourde (max 500 Ko)" });
    const r = new FileReader();
    r.onload = () => setPhoto(r.result);
    r.readAsDataURL(f);
  };

  const changePwd = async () => {
    setPwdMsg(null);
    if (!pwd.current || !pwd.new) return setPwdMsg({ type: "error", text: "Tous les champs sont requis" });
    if (pwd.new.length < 8 || !/\d/.test(pwd.new)) return setPwdMsg({ type: "error", text: "Nouveau mot de passe : 8 car. min dont un chiffre" });
    if (pwd.new !== pwd.confirm) return setPwdMsg({ type: "error", text: "Les mots de passe ne correspondent pas" });
    setPwdSaving(true);
    try {
      await auth.changePassword(pwd.current, pwd.new);
      setPwdMsg({ type: "success", text: "Mot de passe modifié" });
      setPwd({ current: "", new: "", confirm: "" });
    } catch (e) { setPwdMsg({ type: "error", text: e?.response?.data?.detail || "Erreur" }); }
    finally { setPwdSaving(false); }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <Section title="Mon profil" icon="👤" defaultOpen>
        {msg && <Alert type={msg.type}><span>{msg.type === "success" ? "✓" : "⚠"}</span><span>{msg.text}</span></Alert>}
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            {photo ? (
              <img src={photo} alt="Photo" style={{ width: 100, height: 100, borderRadius: "50%", objectFit: "cover", border: `2px solid ${C.gold}` }} />
            ) : (
              <div style={{ width: 100, height: 100, borderRadius: "50%", background: C.gold, color: C.navy, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34, fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>{user.initials}</div>
            )}
            <label style={{ display: "block", marginTop: 8, textAlign: "center", cursor: "pointer" }}>
              <input type="file" accept="image/*" onChange={onPhotoChange} style={{ display: "none" }} />
              <span style={{ fontSize: 11, color: C.teal, fontWeight: 600 }}>Changer</span>
            </label>
          </div>
          <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FormField label="Nom">
              <Input value={user.nom} disabled style={{ background: C.gray2, color: C.mid }} />
            </FormField>
            <FormField label="Email">
              <Input value={user.email} disabled style={{ background: C.gray2, color: C.mid }} />
            </FormField>
            {myConseiller && (
              <FormField label="N° d'agrément">
                <Input value={myConseiller.agrement || myConseiller.id} disabled style={{ background: C.gray2, color: C.mid }} />
              </FormField>
            )}
            {myConseiller && (
              <FormField label="Code promo personnel">
                <Input value={myConseiller.codePromo || "—"} disabled style={{ background: C.gray2, color: C.mid }} />
              </FormField>
            )}
            <FormField label="Téléphone">
              <Input value={tel} onChange={(e) => setTel(e.target.value)} placeholder="+590 690…" />
            </FormField>
            {myConseiller && (
              <FormField label="Formule de cotisation">
                <Input value={({ Conseiller: "Consultant" })[myConseiller.formule] || myConseiller.formule || "—"} disabled style={{ background: C.gray2, color: C.mid }} />
              </FormField>
            )}
          </div>
        </div>
        <Btn variant="gold" disabled={saving} onClick={saveProfile}>{saving ? "…" : "Enregistrer les modifications"}</Btn>
      </Section>

      {myConseiller?.codePromo && <MonLienPerso code={myConseiller.codePromo} />}

      {user?.role !== "admin" && myConseiller && <Section title="Ma cotisation" icon="💳" defaultOpen><MaCotisationBloc /></Section>}
      <Section title="Changer mon mot de passe" icon="🔒" defaultOpen={false}>
        {pwdMsg && <Alert type={pwdMsg.type}><span>{pwdMsg.type === "success" ? "✓" : "⚠"}</span><span>{pwdMsg.text}</span></Alert>}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 400 }}>
          <FormField label="Mot de passe actuel">
            <Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} />
          </FormField>
          <FormField label="Nouveau mot de passe (8 car. min, un chiffre)">
            <Input type="password" value={pwd.new} onChange={(e) => setPwd({ ...pwd, new: e.target.value })} />
          </FormField>
          <FormField label="Confirmez">
            <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} />
          </FormField>
          <div>
            <Btn variant="gold" disabled={pwdSaving} onClick={changePwd}>{pwdSaving ? "…" : "Changer le mot de passe"}</Btn>
          </div>
        </div>
      </Section>
    </div>
  );
};

// ── Modal après création d'un consultant : affiche le lien d'invitation ──────
export const InvitationLinkModal = ({ result, onClose }) => {
  const link = `${window.location.origin}/setup/${result.invitation_token}`;
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const waText = "Bonjour " + (result.conseiller.prenom || result.conseiller.nom) + ", votre compte AUPRÉA est prêt. Définissez votre mot de passe (valable 48h) : " + link;
  const wa = "https://wa.me/?text=" + encodeURIComponent(waText);
  const mailBody = "Bonjour,%0D%0A%0D%0AVotre compte AUPR%C3%89A a %C3%A9t%C3%A9 cr%C3%A9%C3%A9. Cliquez sur le lien ci-dessous pour d%C3%A9finir votre mot de passe (valable 48h) :%0D%0A%0D%0A" + encodeURIComponent(link) + "%0D%0A%0D%0A%C3%80 tr%C3%A8s vite,%0D%0AAUPR%C3%89A";
  const mailHref = "mailto:" + result.conseiller.email + "?subject=" + encodeURIComponent("Activation de votre compte AUPRÉA") + "&body=" + mailBody;
  return (
    <Modal title="✓ Consultant créé" onClose={onClose}
      footer={<Btn variant="gold" onClick={onClose}>Fermer</Btn>}>
      <Alert type="success">
        <span>✓</span>
        <span><strong>{result.conseiller.prenom} {result.conseiller.nom}</strong> · {result.conseiller.agrement} · {result.conseiller.codePromo}</span>
      </Alert>
      <div style={{ background: C.gray2, borderRadius: 10, padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>Lien d'activation à transmettre</div>
        <div style={{ fontSize: 11, fontFamily: "monospace", color: C.navy, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 6, padding: 8, wordBreak: "break-all", marginBottom: 8 }}>{link}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn small variant="gold" onClick={copy}>{copied ? "✓ Copié !" : "📋 Copier le lien"}</Btn>
          <Btn small onClick={() => window.open(wa, "_blank")}>💬 WhatsApp</Btn>
          <Btn small onClick={() => { window.location.href = mailHref; }}>📧 Email</Btn>
        </div>
      </div>
      <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>
        💡 Ce lien est valable 48 heures. Il sera envoyé automatiquement par email dès l'activation du service Resend.
      </div>
    </Modal>
  );
};

// ── Invitations en attente (admin) ───────────────────────────────────────────
export const PendingInvitations = () => {
  const { data: invs, loading, reload } = useAsync(conseillersApi.pendingInvitations, []);
  const { data: resets, loading: l2, reload: r2 } = useAsync(conseillersApi.pendingResets, []);
  if (loading || l2) return <Spinner />;
  const copyLink = (path) => {
    const link = `${window.location.origin}${path}`;
    navigator.clipboard.writeText(link);
    alert("Lien copié : " + link);
  };
  return (
    <>
      <Alert type="info">
        <span>📧</span>
        <span>En attendant l'activation de Resend, retrouvez ici les liens d'invitation et de réinitialisation à transmettre manuellement.</span>
      </Alert>

      <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".09em", margin: "16px 0 10px" }}>Invitations (48h)</div>
      {(invs || []).length === 0 ? (
        <div style={{ textAlign: "center", padding: 30, color: C.muted, background: "#fff", borderRadius: 10, border: `.5px dashed ${C.border}` }}>Aucune invitation en attente</div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: `.5px solid ${C.border}`, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr>{["Email", "Nom", "Créée le", "Expire", ""].map(h => (
              <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", borderBottom: `.5px solid ${C.border}`, background: C.gray2 }}>{h}</th>
            ))}</tr></thead>
            <tbody>
              {invs.map(i => (
                <tr key={i.token} style={{ borderBottom: `.5px solid ${C.border}`, opacity: i.expired ? .5 : 1 }}>
                  <td style={{ padding: "12px 16px", fontWeight: 600 }}>{i.email}</td>
                  <td style={{ padding: "12px 16px", fontSize: 12 }}>{i.nom}</td>
                  <td style={{ padding: "12px 16px", fontSize: 11, color: C.mid }}>{new Date(i.created_at).toLocaleString("fr-FR")}</td>
                  <td style={{ padding: "12px 16px", fontSize: 11, color: i.expired ? C.red : C.mid }}>{i.expired ? "Expirée" : new Date(i.expires_at).toLocaleString("fr-FR")}</td>
                  <td style={{ padding: "12px 16px" }}>
                    {!i.expired && <Btn small variant="gold" onClick={() => copyLink(`/setup/${i.token}`)}>📋 Copier lien</Btn>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".09em", margin: "24px 0 10px" }}>Réinitialisations de mot de passe (30 min)</div>
      {(resets || []).length === 0 ? (
        <div style={{ textAlign: "center", padding: 30, color: C.muted, background: "#fff", borderRadius: 10, border: `.5px dashed ${C.border}` }}>Aucune demande</div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: `.5px solid ${C.border}`, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr>{["Email", "Demandée le", "Expire", ""].map(h => (
              <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", borderBottom: `.5px solid ${C.border}`, background: C.gray2 }}>{h}</th>
            ))}</tr></thead>
            <tbody>
              {resets.map(r => (
                <tr key={r.token} style={{ borderBottom: `.5px solid ${C.border}`, opacity: r.expired ? .5 : 1 }}>
                  <td style={{ padding: "12px 16px", fontWeight: 600 }}>{r.email}</td>
                  <td style={{ padding: "12px 16px", fontSize: 11, color: C.mid }}>{new Date(r.created_at).toLocaleString("fr-FR")}</td>
                  <td style={{ padding: "12px 16px", fontSize: 11, color: r.expired ? C.red : C.mid }}>{r.expired ? "Expiré" : new Date(r.expires_at).toLocaleString("fr-FR")}</td>
                  <td style={{ padding: "12px 16px" }}>
                    {!r.expired && <Btn small variant="gold" onClick={() => copyLink(`/reset/${r.token}`)}>📋 Copier lien</Btn>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <Btn small onClick={() => { reload(); r2(); }}>↻ Rafraîchir</Btn>
      </div>
    </>
  );
};

// ── Zone + role helpers ──────────────────────────────────────────────────────
export const ZONES = ["Guadeloupe", "Martinique", "Guyane", "La Réunion", "Mayotte", "Hexagone", "Étranger"];
export const ROLES_USER = [
  { value: "conseiller", label: "Consultant" },
  { value: "partenaire_recruteur", label: "Partenaire recruteur" },
];
